
import firebase from "firebase/app";
import 'firebase/auth';
import http from "./http";
import session from "./session";

class Auth {
  static async _token() {
    var user = firebase.auth().currentUser;
    var token = user ? await user.getIdToken(true) : ``;
    session.set(`token`, token);
  }

  static login(email, password) {
    return firebase.auth().signInWithEmailAndPassword(email, password);
  }

  /**
   * Signs the user out of this browser.
   *
   * @param revoke also revoke the refresh token server-side. `signOut()` alone
   * only clears local state, leaving the refresh token able to mint new ID
   * tokens. Pass `true` for an explicit logout.
   *
   * Deliberately NOT the default: `revokeRefreshTokens()` is global to the user
   * in Firebase, so firing it from the idle timeout would sign the user out of
   * every other device they are signed in on.
   */
  static logout(revoke = false) {
    // Uses `http` rather than the Api factory: importing Api here would create
    // a cycle (Api -> service -> auth). The URL is resolved the same way.
    var config = session.get(`config`);
    var url = `${config.api.url}/users?_action=logout`;
    // Only call the API when a user is actually signed in; otherwise this fires
    // on every failed login attempt and after the session is already gone.
    var request =
      revoke && firebase.auth().currentUser
        ? http.put(url, {}, undefined).catch(e => console.error(e))
        : Promise.resolve();
    return request.then(() => {
      session.set(`token`, undefined);
      return firebase.auth().signOut();
    });
  }

  static purge() {
    return window.indexedDB.deleteDatabase("firebaseLocalStorageDb");
  }

  static state(fn) {
    if (typeof fn != "function")
      throw new Error(`This method requires a callback function!`);
    return firebase.auth().onAuthStateChanged(fn);
  }

  static token(token = ``) {
    var key = `token`;
    if (token) return session.set(key, token).get(key);
    Auth._token();
    return session.get(key);
  }

  /**
   * Resolves to a usable ID token.
   *
   * `getIdToken()` returns the cached token while it is still valid and
   * silently renews it once it expires, so calling this per request prevents
   * the app from sending a token that is older than Firebase's one hour
   * lifetime (which the API rejected with `auth/id-token-expired`).
   */
  static async current() {
    var user = firebase.auth().currentUser;
    if (!user) return ``;
    try {
      var token = await user.getIdToken();
      session.set(`token`, token);
      return token;
    } catch (e) {
      // The account was signed out, disabled or revoked mid-flight. Let the
      // request go out with the last known token; the API answers 401 and the
      // existing `onAuthStateChanged` flow takes the user back to the login.
      console.error(e);
      return session.get(`token`) || ``;
    }
  }
}

export default Auth;
